import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Wine } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed — cheers!");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer data-testid="footer" className="relative border-t border-white/10 bg-wine-bg">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-6">
            <div className="flex items-center gap-3">
              <Wine className="h-6 w-6 text-gold" />
              <span className="font-display text-2xl text-cream tracking-tight">
                Macarthur <span className="italic text-terracotta">Wine</span> Society
              </span>
            </div>
            <p className="mt-6 max-w-md font-display text-3xl md:text-4xl text-cream leading-tight">
              Never miss a pour.
            </p>
            <form onSubmit={subscribe} className="mt-6 flex max-w-md gap-3">
              <Input
                data-testid="newsletter-email"
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12 bg-wine-surface border-white/15 text-cream"
              />
              <button
                data-testid="newsletter-submit"
                disabled={busy}
                className="whitespace-nowrap rounded-full bg-gold px-6 text-sm font-semibold text-wine-bg transition-all duration-300 hover:bg-cream disabled:opacity-60"
              >
                Subscribe
              </button>
            </form>
          </div>

          <div className="md:col-span-3 md:col-start-9">
            <h4 className="text-xs uppercase tracking-[0.2em] text-smoke">Explore</h4>
            <ul className="mt-5 space-y-3 text-cream/90">
              {[
                ["The Society", "manifesto"],
                ["Gatherings", "events"],
                ["Ideas Board", "board"],
                ["Join", "join"],
              ].map(([label, id]) => (
                <li key={id}>
                  <button
                    onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" })}
                    className="hover:text-gold transition-colors duration-300"
                  >
                    {label}
                  </button>
                </li>
              ))}
            </ul>
            <h4 className="mt-8 text-xs uppercase tracking-[0.2em] text-smoke">Get in touch</h4>
            <a
              data-testid="footer-email"
              href="mailto:cheers@macarthurwinesociety.com.au"
              className="mt-4 inline-block text-cream/90 hover:text-gold transition-colors duration-300 break-all"
            >
              cheers@macarthurwinesociety.com.au
            </a>
          </div>
        </div>

        <div className="mt-16 flex flex-col md:flex-row items-center justify-between gap-4 border-t border-white/10 pt-8 text-sm text-smoke">
          <p>© {new Date().getFullYear()} Macarthur Wine Society · Macarthur, NSW · <a href="mailto:cheers@macarthurwinesociety.com.au" className="hover:text-gold transition-colors">cheers@macarthurwinesociety.com.au</a></p>
          <Link data-testid="admin-link" to="/admin/login" className="hover:text-gold transition-colors duration-300">
            Members' admin
          </Link>
        </div>
      </div>
    </footer>
  );
}
